import { useState, useEffect, useCallback } from 'react';
import {
    fetchSalaryDeductions,
    DeductionRecord,
    DeductionResponse,
    DeductionParams
} from '../api/salary/deduction.service';

interface UseDeductionsResult {
    fetchData: (page: number , fromDate :string, toDate:string) => Promise<void>;
    deductions: DeductionRecord[];
    loading: boolean;
    error: string | null;
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
    pagination: {
        currentPage: number;
        totalPages: number;
        totalCount: number;
        pageSize: number;
    };
    setCurrentPage: (page: number) => void;
    goToNextPage: () => void;
    goToPreviousPage: () => void;
    refetch: () => Promise<void>;
    clearFilters: () => void;
}

interface UseDeductionsFilters {
    year?: string;
    month?: string;
    searchQuery?: string;
    userId?: string;
    fromDate?: string;
    toDate?: string;
}

export const useDeductions = (
    pageSize: number = 10, 
    tab?: number,
    filters?: UseDeductionsFilters
): UseDeductionsResult => {
    const [deductions, setDeductions] = useState<DeductionRecord[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalCount, setTotalCount] = useState<number>(0);

    const fetchData = useCallback(async (
        page: number,
        fromDate?:string,
        toDate?:string
    ) => {
        try {
            setLoading(true);
            setError(null);

            const params: DeductionParams = {
                PageNumber: page,
                PageSize: pageSize,
                fromDate: fromDate,
                toDate : toDate,
                ...(tab !== undefined && { tab }),
            };
            if (fromDate && fromDate.trim() !== '') {
                params.fromDate = fromDate
            } 

            if (toDate && toDate.trim() !== '') {
                params.toDate = toDate

            } else {
                params.toDate = null;
            }

            if (filters?.searchQuery && filters.searchQuery.trim() !== '') {
                params.searchQuery = filters.searchQuery.trim();
            } else {
                params.searchQuery = null;
            }

            if (filters?.userId && filters.userId !== 'all') {
                params.userId = filters.userId;
            } else {
                params.userId = null;
            }
            
            
            const response: DeductionResponse = await fetchSalaryDeductions(params);
            const records = response?.response ?? [];
            setDeductions(records);
            const totalRecords = response?.totalCount ?? records.length;
            const totalPagesFromAPI = response?.numberOfPages ?? Math.ceil(totalRecords / pageSize);
            setTotalCount(totalRecords);
            setTotalPages(totalPagesFromAPI);
        } catch (err) {
            console.error("Failed to fetch deductions:", err);
            setError((err as Error).message || "Failed to load deductions");
            setDeductions([]);
        } finally {
            setLoading(false);
        }
    }, [pageSize, filters, tab]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filters]);

    useEffect(() => {
        fetchData(currentPage);
    }, [currentPage, fetchData]);


    const clearFilters = useCallback(() => {
        setCurrentPage(1);
        fetchData(1);
    }, [fetchData]);

    const goToNextPage = useCallback(() => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    }, [currentPage, totalPages]);

    const goToPreviousPage = useCallback(() => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    }, [currentPage]);

    const refetch = useCallback(async () => {
        await fetchData(currentPage);
    }, [currentPage, fetchData]);

    const pagination = {
        currentPage,
        totalPages,
        totalCount,
        pageSize,
    };

    return {
        deductions,
        fetchData,
        loading,
        error,
        currentPage,
        totalPages,
        totalCount,
        pageSize,
        pagination,
        setCurrentPage,
        goToNextPage,
        goToPreviousPage,
        refetch,
        clearFilters,
};
};